import {
  collection,
  doc,
  getDocs,
  getDoc,
  writeBatch,
  serverTimestamp,
  increment,
  runTransaction
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { MOVEMENT_TYPES } from "../../inventory/models/inventory.js";
import { writeAudit } from "../../../firebase/auditLogger.js";
import { diffPurchase } from "../../invoices/utils/invoiceEdit.js";

const PURCHASES_COLLECTION = "purchases";
const ITEMS_COLLECTION = "items";
const MOVEMENTS_COLLECTION = "inventoryMovements";
const CUSTOMERS_COLLECTION = "customers"; // Vendors are stored in customers collection with partyType='vendor'|'both'

/**
 * Atomically create a purchase bill:
 * 1. Writes purchases/{id} document
 * 2. Increments item stock (stock = stock + qty) for physical goods
 * 3. Records MOVEMENT_TYPES.IN inventory movements with vendor bill notes
 * 4. Increments vendor payable balance (openingBalance = openingBalance + balance)
 * 5. Optionally updates item costPrice with the purchase rate
 */
export async function createPurchaseWithStockInward(purchaseData) {
  const batch = writeBatch(db);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  // 1. Create Purchase Bill Document
  const purchaseRef = doc(collection(db, PURCHASES_COLLECTION));
  const fullPayload = {
    ...purchaseData,
    createdBy: user,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  batch.set(purchaseRef, fullPayload);

  // 2. Increment Item Stock & Record IN Movements
  const lineItems = purchaseData.lineItems || [];
  lineItems.forEach((item) => {
    if (item.itemId && item.itemType !== "service") {
      const itemRef = doc(db, ITEMS_COLLECTION, item.itemId);
      const qty = Number(item.qty || 0);

      // Increment stock and optionally update costPrice
      const itemUpdates = {
        stock: increment(qty),
        updatedAt: serverTimestamp()
      };
      if (item.rate && Number(item.rate) > 0) {
        itemUpdates.costPrice = Number(item.rate);
      }
      batch.update(itemRef, itemUpdates);

      // Record Stock Inward movement
      const movementRef = doc(collection(db, MOVEMENTS_COLLECTION));
      batch.set(movementRef, {
        itemId: item.itemId,
        itemName: item.itemName || "",
        itemSku: item.gstCode || "",
        type: MOVEMENT_TYPES.IN,
        quantity: qty,
        previousStock: null,
        newStock: null,
        reason: "Vendor Purchase Bill",
        notes: `Purchase Bill #${purchaseData.vendorBillNo || "N/A"} from ${purchaseData.vendorName || "Vendor"}`,
        referenceId: purchaseRef.id,
        createdBy: user,
        createdAt: serverTimestamp()
      });
    }
  });

  // 3. Update Vendor Outstanding Payable Balance if bill is unpaid/partial
  if (purchaseData.vendorId && purchaseData.balance > 0) {
    try {
      const vendorRef = doc(db, CUSTOMERS_COLLECTION, purchaseData.vendorId);
      const vendorSnap = await getDoc(vendorRef);
      if (vendorSnap.exists()) {
        batch.update(vendorRef, {
          openingBalance: increment(Number(purchaseData.balance || 0)),
          updatedAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.warn(`Could not update vendor balance for ${purchaseData.vendorId}:`, err);
    }
  }

  try {
    await batch.commit();
  } catch (err) {
    writeAudit("PURCHASE_CREATE_FAILED", {
      vendorBillNo: purchaseData.vendorBillNo,
      vendorId: purchaseData.vendorId,
      vendorName: purchaseData.vendorName,
      grandTotal: purchaseData.grandTotal
    }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("PURCHASE_CREATED", {
    purchaseId: purchaseRef.id,
    vendorBillNo: purchaseData.vendorBillNo,
    vendorId: purchaseData.vendorId,
    vendorName: purchaseData.vendorName,
    grandTotal: purchaseData.grandTotal,
    balance: purchaseData.balance,
    billType: purchaseData.billType,
    lineItemsCount: (purchaseData.lineItems || []).length
  });

  return purchaseRef.id;
}

const millis = (ts) => (ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : null);

/**
 * Saves an edited purchase bill in one transaction (same rules as editing an invoice):
 * keeps the previous version under purchases/{id}/revisions, moves stock by the quantity difference
 * (more qty bought → more stock), adjusts the vendor's payable, keeps recorded payments as they are.
 */
export async function updatePurchase(purchaseId, newData, { expectedUpdatedAt = null, paidInFull = false } = {}) {
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";
  const billRef = doc(db, PURCHASES_COLLECTION, purchaseId);
  let result = null;

  try {
    result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(billRef);
      if (!snap.exists()) throw new Error("Purchase bill not found — it may have been deleted.");
      const old = snap.data();
      const expected = millis(expectedUpdatedAt);
      const current = millis(old.updatedAt);
      if (expected && current && expected !== current) {
        throw new Error("This bill was changed elsewhere (e.g. a payment was recorded). Close and reopen it, then edit again.");
      }

      // A saved bill keeps the company it was booked under
      const fields = { ...newData, companyId: old.companyId || "", companyName: old.companyName || "" };
      const change = diffPurchase(old, { ...fields, paidInFull });
      if (change.customerChanged && change.customerLocked) {
        throw new Error("Payments are recorded to this bill's vendor. Void them before changing the vendor.");
      }
      if (change.overReceived > 0) {
        const nos = change.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ");
        throw new Error(`Paid ₹${change.newPaid.toLocaleString("en-IN")} is more than the new total ₹${change.newTotal.toLocaleString("en-IN")}. Edit or void ${nos || "the payment"} first.`);
      }

      // Reads first (party and item docs must exist), then writes
      const dueRefs = [];
      for (const d of change.dues) {
        const ref = doc(db, CUSTOMERS_COLLECTION, d.customerId);
        if ((await tx.get(ref)).exists()) dueRefs.push({ ref, delta: d.delta });
      }
      const rateByItem = new Map();
      (fields.lineItems || []).forEach((li) => {
        if (li.itemId && li.itemType !== "service" && Number(li.rate) > 0) rateByItem.set(li.itemId, Number(li.rate));
      });
      const itemIds = new Set([...change.stock.map((s) => s.itemId), ...rateByItem.keys()]);
      const liveItems = new Set();
      for (const itemId of itemIds) {
        if ((await tx.get(doc(db, ITEMS_COLLECTION, itemId))).exists()) liveItems.add(itemId);
      }

      // 1. Previous version (read-only history)
      tx.set(doc(collection(db, PURCHASES_COLLECTION, purchaseId, "revisions")), {
        snapshot: old,
        oldGrandTotal: change.oldTotal,
        newGrandTotal: change.newTotal,
        summary: change.summary,
        editedBy: user,
        editedAt: serverTimestamp()
      });

      // 2. Stock follows the quantity difference; cost price follows the bill's rate (as on create)
      const stockByItem = new Map(change.stock.map((s) => [s.itemId, s]));
      liveItems.forEach((itemId) => {
        const s = stockByItem.get(itemId);
        const updates = { updatedAt: serverTimestamp() };
        if (s) updates.stock = increment(s.stockChange);
        if (rateByItem.has(itemId)) updates.costPrice = rateByItem.get(itemId);
        tx.update(doc(db, ITEMS_COLLECTION, itemId), updates);
        if (s) {
          tx.set(doc(collection(db, MOVEMENTS_COLLECTION)), {
            itemId,
            itemName: s.itemName,
            itemSku: "",
            type: s.stockChange < 0 ? MOVEMENT_TYPES.OUT : MOVEMENT_TYPES.IN,
            quantity: Math.abs(s.stockChange),
            previousStock: null,
            newStock: null,
            reason: "Purchase bill edited",
            notes: `Purchase Bill #${old.vendorBillNo || purchaseId}: qty ${s.oldQty} → ${s.newQty}`,
            referenceId: purchaseId,
            createdBy: user,
            createdAt: serverTimestamp()
          });
        }
      });

      // 3. Vendor payable
      dueRefs.forEach(({ ref, delta }) => tx.update(ref, { openingBalance: increment(delta), updatedAt: serverTimestamp() }));

      // 4. The bill — recorded payments stay as they are
      tx.update(billRef, {
        ...fields,
        paid: change.newPaid,
        balance: change.newBalance,
        paymentStatus: change.paymentStatus,
        paymentHistory: old.paymentHistory || [],
        editCount: increment(1),
        lastEditedAt: serverTimestamp(),
        lastEditedBy: user,
        updatedAt: serverTimestamp()
      });

      return { vendorBillNo: fields.vendorBillNo || old.vendorBillNo, change };
    });
  } catch (err) {
    writeAudit("PURCHASE_EDIT_FAILED", { purchaseId, vendorBillNo: newData.vendorBillNo }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("PURCHASE_EDITED", {
    purchaseId,
    vendorBillNo: result.vendorBillNo,
    oldGrandTotal: result.change.oldTotal,
    newGrandTotal: result.change.newTotal,
    summary: result.change.summary.join("; ")
  });
  return result;
}

/** Earlier versions of an edited purchase bill, newest first. */
export async function getPurchaseRevisions(purchaseId) {
  const snap = await getDocs(collection(db, PURCHASES_COLLECTION, purchaseId, "revisions"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (millis(b.editedAt) || 0) - (millis(a.editedAt) || 0));
}

/** One purchase bill (fresh from the database). */
export async function getPurchaseById(purchaseId) {
  const snap = await getDoc(doc(db, PURCHASES_COLLECTION, purchaseId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Fetch all purchases ordered by creation date descending
 */
export async function getPurchases() {
  // Single read of the whole collection (an orderBy("createdAt") query would silently
  // drop legacy bills without createdAt), sorted newest first in JS.
  const snapshot = await getDocs(collection(db, PURCHASES_COLLECTION));
  return snapshot.docs
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt
      };
    })
    .sort((a, b) => String(b.createdAt || b.billDate || "").localeCompare(String(a.createdAt || a.billDate || "")));
}

/**
 * Record a payment made to a vendor against a purchase bill
 */
export async function updatePurchasePayment(purchaseId, { paidAmount, vendorId }) {
  const docRef = doc(db, PURCHASES_COLLECTION, purchaseId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error("Purchase bill not found.");

  const current = snap.data();
  const currentPaid = Number(current.paid || 0);
  const total = Number(current.grandTotal || 0);
  const additionalPayment = Number(paidAmount || 0);

  const nextPaid = currentPaid + additionalPayment;
  const nextBalance = Math.max(0, total - nextPaid);
  const nextStatus = nextBalance === 0 ? "Paid" : "Partial";

  const batch = writeBatch(db);
  batch.update(docRef, {
    paid: nextPaid,
    balance: nextBalance,
    paymentStatus: nextStatus,
    updatedAt: serverTimestamp()
  });

  // Reduce vendor payable balance
  const targetVendorId = vendorId || current.vendorId;
  if (targetVendorId && additionalPayment > 0) {
    const vendorRef = doc(db, CUSTOMERS_COLLECTION, targetVendorId);
    batch.update(vendorRef, {
      openingBalance: increment(-additionalPayment),
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();

  writeAudit("PURCHASE_PAYMENT_RECORDED", {
    purchaseId,
    vendorBillNo: current.vendorBillNo,
    vendorId: targetVendorId,
    vendorName: current.vendorName,
    additionalPayment,
    nextPaid,
    nextBalance,
    nextStatus
  });

  return { nextPaid, nextBalance, nextStatus };
}

/**
 * Atomically deletes a purchase bill and reverses inward stock,
 * creating OUT audit movements and reducing vendor balance.
 */
export async function deletePurchaseWithStockReversal(purchaseId) {
  const docRef = doc(db, PURCHASES_COLLECTION, purchaseId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error("Purchase bill not found.");

  const purchase = snap.data();
  const batch = writeBatch(db);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  // 1. Reverse item stock & create OUT movements
  const lineItems = purchase.lineItems || [];
  for (const item of lineItems) {
    if (item.itemId && item.itemType !== "service") {
      try {
        const itemRef = doc(db, ITEMS_COLLECTION, item.itemId);
        const itemSnap = await getDoc(itemRef);
        if (itemSnap.exists()) {
          const qty = Number(item.qty || 0);
          batch.update(itemRef, {
            stock: increment(-qty),
            updatedAt: serverTimestamp()
          });

          const movementRef = doc(collection(db, MOVEMENTS_COLLECTION));
          batch.set(movementRef, {
            itemId: item.itemId,
            itemName: item.itemName || "",
            itemSku: item.gstCode || "",
            type: MOVEMENT_TYPES.OUT,
            quantity: qty,
            previousStock: null,
            newStock: null,
            reason: "Purchase Bill Cancellation",
            notes: `Stock reversed from cancelled purchase bill ${purchase.vendorBillNo || purchaseId}`,
            referenceId: purchaseId,
            createdBy: user,
            createdAt: serverTimestamp()
          });
        }
      } catch (err) {
        console.warn(`Could not reverse stock for item ${item.itemId}:`, err);
      }
    }
  }

  // 2. Adjust vendor balance if purchase had unpaid balance
  const unpaidBalance = Number(purchase.balance || 0);
  if (purchase.vendorId && unpaidBalance > 0) {
    try {
      const vendorRef = doc(db, CUSTOMERS_COLLECTION, purchase.vendorId);
      const vendorSnap = await getDoc(vendorRef);
      if (vendorSnap.exists()) {
        batch.update(vendorRef, {
          openingBalance: increment(-unpaidBalance),
          updatedAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.warn(`Could not adjust vendor balance for ${purchase.vendorId}:`, err);
    }
  }

  // 3. Delete purchase document
  batch.delete(docRef);

  try {
    await batch.commit();
  } catch (err) {
    writeAudit("PURCHASE_DELETE_FAILED", {
      purchaseId,
      vendorBillNo: purchase.vendorBillNo,
      vendorId: purchase.vendorId,
      vendorName: purchase.vendorName,
      grandTotal: purchase.grandTotal
    }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("PURCHASE_DELETED", {
    purchaseId,
    vendorBillNo: purchase.vendorBillNo,
    vendorId: purchase.vendorId,
    vendorName: purchase.vendorName,
    grandTotal: purchase.grandTotal,
    unpaidBalance,
    lineItemsCount: lineItems.length
  });

  return purchaseId;
}
